import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '../components/ui/select';
import { Save, FileText, User, Phone, AlertCircle, CheckCircle2, ClipboardList } from 'lucide-react';

const AREAS_OF_CONCERN = [
  'Academic Achievement', 'Behavior', 'Communication / Speech-Language',
  'Social / Emotional', 'Motor Skills', 'Health / Medical',
  'Adaptive / Daily Living', 'Sensory (Vision / Hearing)', 'Other',
];

const REFERRAL_SOURCES = [
  'Parent / Guardian', 'Teacher', 'School Team', 'Administrator',
  'Physician / Medical', 'Self-Referral', 'Community Agency', 'Other',
];

const NOTIFICATION_METHODS = ['Phone', 'Email', 'Letter / Mail', 'In Person', 'Other'];

function mergeContent(existing) {
  const defaults = {
    referral_date: '', referral_source: '', reason: '',
    areas_of_concern: [], interventions_tried: '',
    parent_notification: { notified: false, date: '', method: '' },
    student_grade: '',
    referring_person: { name: '', role: '', contact: '' },
    notes: '',
  };
  if (!existing || typeof existing !== 'object') return defaults;
  return {
    ...defaults, ...existing,
    areas_of_concern: existing.areas_of_concern || [],
    parent_notification: { ...defaults.parent_notification, ...(existing.parent_notification || {}) },
    referring_person: { ...defaults.referring_person, ...(existing.referring_person || {}) },
  };
}

export default function ReferralEditor({ document, student, onSave, saving }) {
  const [content, setContent] = useState(() => mergeContent(document?.content));
  useEffect(() => { setContent(mergeContent(document?.content)); }, [document?.document_id]);

  const update = (key, value) => setContent(prev => ({ ...prev, [key]: value }));
  const updateNested = (parent, key, value) => setContent(prev => ({
    ...prev, [parent]: { ...prev[parent], [key]: value },
  }));
  const toggleArea = (area) => {
    setContent(prev => ({
      ...prev,
      areas_of_concern: prev.areas_of_concern.includes(area)
        ? prev.areas_of_concern.filter(a => a !== area)
        : [...prev.areas_of_concern, area],
    }));
  };
  const handleSave = () => onSave(content);

  return (
    <div className="max-w-4xl space-y-6" data-testid="referral-editor">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-outfit text-xl font-semibold text-[#1C1C1C]" data-testid="referral-title">Referral</h2>
          <p className="text-sm text-[#595959] mt-1">Document the referral for special education evaluation.</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-[#5A8A5A] hover:bg-[#4A7A4A] text-white rounded-xl shrink-0" data-testid="referral-save-top">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save'}
        </Button>
      </div>

      {/* Student Info Banner */}
      {student && (
        <div className="bg-[#F3F4F1] border border-[#E5E5E5] rounded-2xl p-5" data-testid="referral-student-info">
          <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959] mb-3">Student Information</h3>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-sm">
            <div><span className="text-xs text-[#595959]">Name</span><p className="font-medium text-[#1C1C1C]">{student.first_name} {student.last_name}</p></div>
            <div><span className="text-xs text-[#595959]">Grade</span><p className="font-medium text-[#1C1C1C]">{student.grade || '—'}</p></div>
            <div><span className="text-xs text-[#595959]">DOB</span><p className="font-medium text-[#1C1C1C]">{student.date_of_birth || '—'}</p></div>
            <div><span className="text-xs text-[#595959]">Student ID</span><p className="font-medium text-[#1C1C1C]">{student.student_id?.slice(-8) || '—'}</p></div>
          </div>
        </div>
      )}

      {/* Referral Details */}
      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="referral-details-section">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959] mb-4">
          <FileText className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />Referral Details
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          <div>
            <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Referral Date</label>
            <Input type="date" value={content.referral_date} onChange={e => update('referral_date', e.target.value)} className="rounded-xl border-[#E5E5E5] text-sm h-9" data-testid="referral-date" />
          </div>
          <div>
            <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Referral Source</label>
            <Select value={content.referral_source || undefined} onValueChange={v => update('referral_source', v)}>
              <SelectTrigger className="rounded-xl text-sm h-9" data-testid="referral-source"><SelectValue placeholder="Select source" /></SelectTrigger>
              <SelectContent>{REFERRAL_SOURCES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Student Grade at Referral</label>
            <Input value={content.student_grade} onChange={e => update('student_grade', e.target.value)} placeholder="e.g., 3rd" className="rounded-xl border-[#E5E5E5] text-sm h-9" data-testid="referral-grade" />
          </div>
        </div>
        <div>
          <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Reason for Referral</label>
          <Textarea value={content.reason} onChange={e => update('reason', e.target.value)} placeholder="Describe why this student is being referred for evaluation..." rows={4} className="rounded-xl border-[#E5E5E5] text-sm" data-testid="referral-reason" />
        </div>
      </div>

      {/* Areas of Concern */}
      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="referral-areas-section">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959] mb-4">
          <AlertCircle className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />Suspected Areas of Concern
        </h3>
        <p className="text-xs text-[#595959] mb-3">Select all areas where the student may be experiencing difficulty.</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
          {AREAS_OF_CONCERN.map(area => {
            const checked = content.areas_of_concern.includes(area);
            return (
              <label key={area} className={`flex items-center gap-3 px-3 py-2.5 rounded-xl border cursor-pointer transition-colors ${checked ? 'bg-[#5A8A5A]/8 border-[#5A8A5A]/30 text-[#1C1C1C]' : 'border-[#E5E5E5] text-[#595959] hover:bg-[#F3F4F1]'}`} data-testid={`area-${area.toLowerCase().replace(/[^a-z]/g, '-')}`}>
                <input type="checkbox" checked={checked} onChange={() => toggleArea(area)} className="rounded text-[#5A8A5A] focus:ring-[#5A8A5A]" />
                <span className="text-sm">{area}</span>
              </label>
            );
          })}
        </div>
        {content.areas_of_concern.length > 0 && (
          <div className="mt-3 flex flex-wrap gap-1.5">
            {content.areas_of_concern.map(a => <Badge key={a} variant="outline" className="text-xs border-[#5A8A5A]/30 text-[#5A8A5A] bg-[#5A8A5A]/5">{a}</Badge>)}
          </div>
        )}
      </div>

      {/* Interventions Tried */}
      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="referral-interventions-section">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959] mb-4">
          <ClipboardList className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />Interventions Attempted (MTSS / Pre-Referral)
        </h3>
        <Textarea value={content.interventions_tried} onChange={e => update('interventions_tried', e.target.value)} placeholder="Describe interventions, duration, frequency, and outcomes prior to referral (e.g., Tier 2 reading intervention for 8 weeks)..." rows={4} className="rounded-xl border-[#E5E5E5] text-sm" data-testid="referral-interventions" />
      </div>

      {/* Referring Person */}
      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="referral-person-section">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959] mb-4">
          <User className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />Referring Person
        </h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div>
            <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Name</label>
            <Input value={content.referring_person.name} onChange={e => updateNested('referring_person', 'name', e.target.value)} placeholder="Full name" className="rounded-xl border-[#E5E5E5] text-sm h-9" data-testid="referring-person-name" />
          </div>
          <div>
            <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Role / Title</label>
            <Input value={content.referring_person.role} onChange={e => updateNested('referring_person', 'role', e.target.value)} placeholder="e.g., 3rd Grade Teacher" className="rounded-xl border-[#E5E5E5] text-sm h-9" data-testid="referring-person-role" />
          </div>
          <div>
            <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Contact</label>
            <Input value={content.referring_person.contact} onChange={e => updateNested('referring_person', 'contact', e.target.value)} placeholder="Email or phone" className="rounded-xl border-[#E5E5E5] text-sm h-9" data-testid="referring-person-contact" />
          </div>
        </div>
      </div>

      {/* Parent Notification */}
      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="referral-parent-section">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959] mb-4">
          <Phone className="h-3.5 w-3.5 inline mr-1.5 -mt-0.5" />Parent / Guardian Notification
        </h3>
        <div className="mb-3">
          <label className="flex items-center gap-3 cursor-pointer">
            <input type="checkbox" checked={content.parent_notification.notified} onChange={e => updateNested('parent_notification', 'notified', e.target.checked)} className="rounded text-[#5A8A5A] focus:ring-[#5A8A5A] h-4 w-4" data-testid="parent-notified-checkbox" />
            <span className="text-sm text-[#1C1C1C]">Parent / guardian has been notified of this referral</span>
          </label>
        </div>
        {content.parent_notification.notified && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-3 pl-7">
            <div>
              <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Notification Date</label>
              <Input type="date" value={content.parent_notification.date} onChange={e => updateNested('parent_notification', 'date', e.target.value)} className="rounded-xl border-[#E5E5E5] text-sm h-9" data-testid="parent-notification-date" />
            </div>
            <div>
              <label className="text-xs font-medium text-[#1C1C1C] mb-1 block">Notification Method</label>
              <Select value={content.parent_notification.method || undefined} onValueChange={v => updateNested('parent_notification', 'method', v)}>
                <SelectTrigger className="rounded-xl text-sm h-9" data-testid="parent-notification-method"><SelectValue placeholder="Select method" /></SelectTrigger>
                <SelectContent>{NOTIFICATION_METHODS.map(m => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>

      {/* Additional Notes */}
      <div className="bg-white border border-[#E5E5E5] rounded-2xl p-5" data-testid="referral-notes-section">
        <h3 className="text-xs font-bold uppercase tracking-wider text-[#595959] mb-4">Additional Notes</h3>
        <Textarea value={content.notes} onChange={e => update('notes', e.target.value)} placeholder="Any additional information relevant to this referral..." rows={3} className="rounded-xl border-[#E5E5E5] text-sm" data-testid="referral-notes" />
      </div>

      <div className="flex justify-end pt-4 border-t border-[#E5E5E5]">
        <Button onClick={handleSave} disabled={saving} className="bg-[#5A8A5A] hover:bg-[#4A7A4A] text-white rounded-xl" data-testid="referral-save-bottom">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Referral'}
        </Button>
      </div>
    </div>
  );
}
